import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './LeCabinet.css';

const LeCabinet = () => {
  const sectionTwoRef = useRef(null);
  const sectionThreeRef = useRef(null);
  const sectionFourRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px'
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.classList.contains('animated')) {
          entry.target.classList.add('animated');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    if (sectionTwoRef.current) observer.observe(sectionTwoRef.current);
    if (sectionThreeRef.current) observer.observe(sectionThreeRef.current);
    if (sectionFourRef.current) observer.observe(sectionFourRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="le-cabinet">
      {/* Hero Section */}
      <section className="cabinet-hero dark-section">
        <div className="cabinet-hero-content">
          <div className="hero-left">
            <h1 className="title">Un Cabinet Fondé sur la Rigueur et l'Expérience.</h1>
            <div className="title-line"></div>
            <p className="text">
              Le cabinet s'inscrit dans une tradition de pratique du droit reposant sur la discipline, l'exigence et la défense des intérêts de ses clients devant toutes les juridictions.
            </p>
            <Link to="/contact" className="btn-secondary">Consultation</Link>
          </div>
          <div className="hero-right">
            <div className="text-line"></div>
            <p className="text">
              Fondé depuis 1980 par Maître Taoufik Ben Nasr, universitaire reconnu et avocat expérimenté, le cabinet s'appuie sur une approche du droit qui conjugue recherche académique et pratique contentieuse.
              <br /><br />
              Au fil des années, le cabinet a développé une expertise lui permettant d'intervenir dans des affaires juridiques complexes impliquant des particuliers, des entreprises et des institutions.
            </p>
            <img
              src="https://cabinet-ben-nasr.s3.eu-central-1.amazonaws.com/Cabinet1.png"
              alt="Cabinet"
              className="hero-image"
            />
            <Link to="/contact" className="btn-secondary btn-secondary-mobile">Consultation</Link>
          </div>
        </div>
      </section>

      {/* Second Section */}
      <section className="cabinet-section-two" ref={sectionTwoRef}>
        <div className="section-content">
          <div className="image-container">
            <img
              src="https://cabinet-ben-nasr.s3.eu-central-1.amazonaws.com/cabinet2.png"
              alt="Multigénérationnel"
            />
          </div>
          <div className="text-container">
            <h2 className="title">Une Approche Multigénérationnelle</h2>
            <p className="text">
              L'organisation du cabinet repose sur la collaboration entre plusieurs juristes, permettant de conjuguer expérience académique, pratique et vision contemporaine du conseil juridique.
              <br /><br />
              Maître Mehdi Ben Nasr, avocat à la Cour de Cassation et Maître Amine Ben Nasr, interviennent ensemble dans la gestion de dossiers juridiques complexes et dans l'élaboration de stratégies juridiques adaptées aux enjeux spécifiques de chaque affaire.
              <br /><br />
              Le cabinet adopte ainsi une approche collaborative qui garantit une analyse juridique approfondie et une gestion structurée toujours adaptée aux différents dossiers.
            </p>
            <Link to="/contact" className="btn-primary">Contactez-Nous</Link>
          </div>
        </div>
      </section>

      {/* Third Section */}
      <section className="cabinet-section-three dark-section" ref={sectionThreeRef}>
        <div className="section-content">
          <div className="text-container">
            <h2 className="title">Une Dimension Internationale</h2>
            <p className="text">
              Ben Nasr Law Firm accompagne également des investisseurs étrangers, des entreprises internationales et des clients opérant dans des opérations transfrontalières souhaitant bénéficier d'un accompagnement juridique en Tunisie.
              <br /><br />
              Le cabinet assiste et conseille ses clients internationaux concernant l'environnement juridique, réglementaire et institutionnel tunisien, notamment dans le cadre d'opérations commerciales, de conformité et de procédures de règlement des différends.
              <br /><br />
              Cette capacité à articuler maîtrise du droit tunisien et compréhension des standards juridiques internationaux permet au cabinet d'accompagner efficacement les clients opérant dans des contextes internationaux.
            </p>
            <Link to="/contact" className="btn-secondary">Contactez-Nous</Link>
          </div>
          <div className="image-container">
            <img
              src="https://cabinet-ben-nasr.s3.eu-central-1.amazonaws.com/Cabinet3.png"
              alt="International"
            />
          </div>
        </div>
      </section>

      {/* Fourth Section - Philosophy */}
      <section className="cabinet-section-four" ref={sectionFourRef}>
        <div className="philosophy-content">
          <div className="philosophy-tag">Notre philosophie</div>
          <div className="philosophy-line"></div>
          <h2 className="title">L'Activité du Cabinet Repose sur des Principes Fondamentaux :</h2>
          <p className="text philosophy-text">
            Grâce à cette approche, le cabinet s'impose comme conseiller juridique efficace pour les particuliers, les entreprises et les acteurs internationaux opérant en Tunisie.
          </p>
          <div className="principles-grid">
            <div className="principle-card">
              <div className="principle-icon">
                <img
                  src="https://cabinet-ben-nasr.s3.eu-central-1.amazonaws.com/business-deal+(1)+1.svg"
                  alt="Intégrité"
                />
              </div>
              <h3 className="principle-title">Intégrité Professionnelle</h3>
              <p className="principle-text">
                Respect strict des principes d'éthique, d'indépendance et de confidentialité.
              </p>
            </div>
            <div className="principle-card">
              <div className="principle-icon">
                <img
                  src="https://cabinet-ben-nasr.s3.eu-central-1.amazonaws.com/equality+1.svg"
                  alt="Rigueur"
                />
              </div>
              <h3 className="principle-title">Rigueur Juridique</h3>
              <p className="principle-text">
                Analyse juridique approfondie fondée sur une méthodologie rigoureuse.
              </p>
            </div>
            <div className="principle-card">
              <div className="principle-icon">
                <img
                  src="https://cabinet-ben-nasr.s3.eu-central-1.amazonaws.com/shield-check+1.svg"
                  alt="Engagement"
                />
              </div>
              <h3 className="principle-title">Engagement Envers le Client</h3>
              <p className="principle-text">
                Accompagnement stratégique et représentation efficace dans la défense des intérêts des clients.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeCabinet;
